import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography } from '@mui/material';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import DashboardPendingTasksPanel from './DashboardPendingTasksPanel';
import {
  getPendingTasksRailPanelShellSx,
  getPendingTasksRailPanelOuterSx,
  getPendingTasksStackedExpandBarSx,
  getPendingTasksStackedPanelWrapSx,
  getPendingTasksTabButtonSx,
  getPendingTasksTabFloatWrapperSx,
  getPendingTasksTabShellSx,
  readPendingTasksRailCollapsed,
  scheduleAfterRailTransition,
  usePendingTasksTabTop,
  usePrefersReducedMotion,
  writePendingTasksRailCollapsed,
} from '../../utils/dashboardPendingTasksRail';
import { resolveDashboardThemeMode } from '../../utils/dashboardSurfaceSx';

function PendingTasksTab({ taskCount, isDark, onExpand, top, visible, reducedMotion }) {
  return (
    <Box sx={getPendingTasksTabShellSx({ top, visible, reducedMotion, stackedLayout: false })}>
      <Box sx={getPendingTasksTabFloatWrapperSx({ visible, reducedMotion })}>
        <Box
          component="button"
          type="button"
          aria-label={`Show pending tasks${taskCount > 0 ? `, ${taskCount} items` : ''}`}
          aria-expanded="false"
          onClick={onExpand}
          sx={getPendingTasksTabButtonSx({ isDark, reducedMotion })}
        >
        {taskCount > 0 ? (
          <Box
            sx={{
              minWidth: 20,
              height: 20,
              px: 0.5,
              borderRadius: 999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f59e0b',
              color: '#111827',
              fontSize: '0.62rem',
              fontWeight: 800,
              lineHeight: 1,
              boxShadow: '0 0 0 2px rgba(245,158,11,0.25)',
            }}
          >
            {taskCount}
          </Box>
        ) : null}
        <Typography
          component="span"
          sx={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            fontSize: '0.68rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            lineHeight: 1,
          }}
        >
          Tasks
        </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function PendingTasksStackedExpandBar({ taskCount, isDark, onExpand, visible, reducedMotion }) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={`Show pending tasks${taskCount > 0 ? `, ${taskCount} items` : ''}`}
      aria-expanded="false"
      onClick={onExpand}
      sx={getPendingTasksStackedExpandBarSx({ isDark, reducedMotion, visible })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.2 }}>
          Pending Tasks
        </Typography>
        {taskCount > 0 ? (
          <Box
            sx={{
              minWidth: 22,
              height: 22,
              px: 0.75,
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f59e0b',
              color: '#111827',
              fontSize: '0.68rem',
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            {taskCount}
          </Box>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'inherit', flexShrink: 0 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, opacity: 0.82 }}>
          Show
        </Typography>
        <KeyboardDoubleArrowUpIcon sx={{ fontSize: 20 }} />
      </Box>
    </Box>
  );
}

function PendingTasksTabPortal({ children, enabled }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!enabled || !mounted || typeof document === 'undefined') {
    return null;
  }

  return createPortal(children, document.body);
}

export default function DashboardPendingTasksRail({
  themeMode = 'dark',
  refreshKey = 0,
  tasksData,
  tasksLoading = false,
  tasksRefreshing = false,
  onRefreshTasks,
  onCheckout,
  onCheckIn,
  onPayment,
  onHousekeeping,
  onOpenWebCheckInInvite,
  collapsed: collapsedProp,
  onCollapsedChange,
  stackedLayout = false,
}) {
  const [internalCollapsed, setInternalCollapsed] = useState(() => readPendingTasksRailCollapsed());
  const collapsed = collapsedProp ?? internalCollapsed;
  const [taskCount, setTaskCount] = useState(0);
  const [tabVisible, setTabVisible] = useState(collapsed);
  const reducedMotion = usePrefersReducedMotion();
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';

  const setCollapsed = useCallback(
    (nextCollapsed) => {
      if (collapsedProp === undefined) {
        setInternalCollapsed(nextCollapsed);
      }
      writePendingTasksRailCollapsed(nextCollapsed);
      onCollapsedChange?.(nextCollapsed);
    },
    [collapsedProp, onCollapsedChange],
  );

  const handleCollapse = useCallback(() => {
    setTabVisible(false);
    setCollapsed(true);
  }, [setCollapsed]);

  const handleExpand = useCallback(() => {
    setTabVisible(false);
    setCollapsed(false);
  }, [setCollapsed]);

  useEffect(() => {
    if (!collapsed) {
      setTabVisible(false);
      return undefined;
    }

    if (reducedMotion) {
      setTabVisible(true);
      return undefined;
    }

    return scheduleAfterRailTransition(() => {
      setTabVisible(true);
    });
  }, [collapsed, reducedMotion]);

  const handleMetricsChange = useCallback(({ totalCount }) => {
    setTaskCount(Number(totalCount) || 0);
  }, []);

  const tabTop = usePendingTasksTabTop({ enabled: !stackedLayout && collapsed && tabVisible, refreshKey });

  return (
    <>
      {!stackedLayout ? (
        <PendingTasksTabPortal enabled={collapsed}>
          <PendingTasksTab
            taskCount={taskCount}
            isDark={isDark}
            onExpand={handleExpand}
            top={tabTop}
            visible={tabVisible}
            reducedMotion={reducedMotion}
          />
        </PendingTasksTabPortal>
      ) : null}

      {stackedLayout && collapsed ? (
        <PendingTasksStackedExpandBar
          taskCount={taskCount}
          isDark={isDark}
          onExpand={handleExpand}
          visible={tabVisible}
          reducedMotion={reducedMotion}
        />
      ) : null}

      <Box
        sx={
          stackedLayout
            ? getPendingTasksStackedPanelWrapSx({ collapsed, reducedMotion })
            : getPendingTasksRailPanelOuterSx()
        }
      >
        <Box sx={getPendingTasksRailPanelShellSx(stackedLayout)}>
          <DashboardPendingTasksPanel
            themeMode={themeMode}
            refreshKey={refreshKey}
            tasksData={tasksData}
            tasksLoading={tasksLoading}
            tasksRefreshing={tasksRefreshing}
            onRefreshTasks={onRefreshTasks}
            onCheckout={onCheckout}
            onCheckIn={onCheckIn}
            onPayment={onPayment}
            onHousekeeping={onHousekeeping}
            onOpenWebCheckInInvite={onOpenWebCheckInInvite}
            showCollapseControl
            stackedLayout={stackedLayout}
            onCollapse={handleCollapse}
            onMetricsChange={handleMetricsChange}
          />
        </Box>
      </Box>
    </>
  );
}
